import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2, X } from 'lucide-react';
import * as React from 'react';

import { StatusChip } from '@/design';
import { LoadError, useAdminData } from '@/domains/admin';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import {
  TEAM_GROUPS,
  type ContentTeamGroup,
  type IContentTeamMember,
  type IContentTeamMemberBody
} from '../api/team-contracts';
import { teamApi } from '../api/team-api';
import { ImagePicker, Thumb } from '../library';
import { EditPresenceNotice, useEditPresence } from '../presence';
import type { IContentPresence } from '../api/presence-api';
import { BodyEditor } from '../rich-text';
import { ConflictNotice, EmptyNote, HistoryPanel, StatusPill, personName } from '../shared';
import { ContentPage } from './content-page';

/**
 * The team page (WEB-07), over `/api/v1/admin/website/team`.
 *
 * **Order is moved, never typed.** AC3 asks for direct manipulation rather than a numeric sort
 * field, and the reason is in the requirement's own business context: position on this page
 * carries seniority signalling, so it gets argued about and re-argued. Rows drag, and the same
 * move is on two buttons beside each row — a drag is not reachable from a keyboard, and the one
 * place a marketing team will reorder a page is not worth making mouse-only.
 *
 * **A move sends the whole arrangement and redraws from the answer.** survey-api's order route
 * takes the complete live list, so the page can never be left half-sorted; it takes no `If-Match`
 * because an arrangement belongs to the page rather than to a person. A list that no longer
 * matches — somebody added or archived a person while this screen was open — is refused, and the
 * refusal says to read the page again rather than guessing which arrangement was meant.
 *
 * **A person is edited where they sit.** The editor opens directly under the row it belongs to
 * rather than at the foot of the list, so the row being changed and the form changing it are
 * never separated by eight other people. The row stays marked while its form is open.
 *
 * **Removing is archiving.** AC4 asks that a departure leave the live page and stay auditable;
 * deleting the row would satisfy the first half and destroy the second. "Remove from the page"
 * archives, the people below close the gap, and the person stays readable below the fold.
 *
 * A photograph travels with the person and is changed on its own, which is AC2's other half.
 */
export function ContentTeamPage() {
  const can = useCan();
  const { data, error, reload, adopt } = useAdminData<IContentTeamMember[]>((signal) =>
    teamApi.list(undefined, signal)
  );
  const [open, setOpen] = React.useState<Nullable<string>>(null);
  // Here rather than in the panel: the panel is keyed by revision and remounts on every save,
  // which would leave and re-mark the person each time.
  const others = useEditPresence('member', open);
  const [dragging, setDragging] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const page = data ?? [];
  const live = page.filter((member) => member.status !== 'archived');
  const archived = page.filter((member) => member.status === 'archived');

  /** The whole live order as this screen now holds it, sent in one write with the order it was
   *  moved from, so a colleague's reorder is refused rather than undone (design §4b). */
  const reorder = async (memberIds: string[]) => {
    setBusy(true);
    setRefusal(null);
    try {
      adopt(await teamApi.reorder(memberIds, live.map((member) => member.memberId)));
    } catch (moveError) {
      if (moveError instanceof ApiError && moveError.isProblem(ProblemType.invalidTransition)) {
        setRefusal(
          'Somebody else changed the page while this screen was open, so the new order was not ' +
            'applied. Reload it and move them again.'
        );
      } else {
        setRefusal('The order was not saved. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const moveBy = (memberId: string, by: number) => {
    const from = live.findIndex((member) => member.memberId === memberId);
    const to = from + by;
    if (from < 0 || to < 0 || to >= live.length) return;
    const next = live.map((member) => member.memberId);
    next.splice(to, 0, ...next.splice(from, 1));
    void reorder(next);
  };

  const dropOn = (targetId: string) => {
    if (!dragging || dragging === targetId) return setDragging(null);
    const next = live.map((member) => member.memberId).filter((id) => id !== dragging);
    next.splice(
      live.findIndex((member) => member.memberId === targetId),
      0,
      dragging
    );
    setDragging(null);
    void reorder(next);
  };

  const add = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const { body } = await teamApi.create({
        name: 'New person',
        roleTitle: '',
        groups: [],
        bio: '',
        photoImageId: null
      });
      await reload();
      setOpen(body.memberId);
    } catch {
      setRefusal('The person was not added. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <ContentPage title="Team" lede="Who appears on /team, and in what order.">
        <LoadError message={error} />
      </ContentPage>
    );
  }

  return (
    <ContentPage
      title="Team"
      lede="Who appears on /team, and in what order."
      actions={
        <Button size="sm" onClick={add} disabled={busy || !data}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Add a person
        </Button>
      }
    >
      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>Not saved</AlertTitle>
          <AlertDescription className="grid gap-2">
            <p>{refusal}</p>
            <div>
              <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
                Reload the page
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <section className="grid gap-1">
        <h2 className="sr-only">On the page</h2>
        {!data && <EmptyNote>Loading…</EmptyNote>}
        {data && live.length === 0 && <EmptyNote>Nobody is on the team page yet.</EmptyNote>}
        {live.map((member, index) => (
          <div key={member.memberId} className="grid">
            <div
              draggable={!busy}
              onDragStart={() => setDragging(member.memberId)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(member.memberId)}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-md border px-3 py-2',
                dragging === member.memberId && 'opacity-50',
                open === member.memberId
                  ? 'rounded-b-none border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/50 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
                  : 'border-border'
              )}
            >
              <GripVertical
                className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground"
                aria-hidden
              />
              <span className="w-6 shrink-0 text-xs text-muted-foreground tabular-nums">
                {member.position}
              </span>
              <Thumb image={member.photo} size="sm" className="shrink-0" />
              <div className="grid min-w-0 flex-1 gap-0.5">
                <span className="truncate font-medium text-[var(--color-royal-blue)]">
                  {member.name}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {member.roleTitle || 'No role set'}
                  {member.groups.length > 0 && ` · ${groupLabels(member.groups)}`}
                </span>
              </div>
              <StatusPill status={member.status} />
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${member.name} up`}
                  disabled={index === 0 || busy}
                  onClick={() => moveBy(member.memberId, -1)}
                >
                  <ChevronUp className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${member.name} down`}
                  disabled={index === live.length - 1 || busy}
                  onClick={() => moveBy(member.memberId, 1)}
                >
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </Button>
                {/* Named for the person as the two move buttons are: a page of ten draws ten
                    of these, and "Edit" ten times over tells a screen reader nothing. The
                    visible word stays inside the accessible name, so the two agree. */}
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={
                    open === member.memberId ? `Close ${member.name}` : `Edit ${member.name}`
                  }
                  onClick={() => setOpen(open === member.memberId ? null : member.memberId)}
                >
                  {open === member.memberId ? 'Close' : 'Edit'}
                </Button>
              </div>
            </div>
            {open === member.memberId && (
              <MemberEditor
                key={`${member.memberId}:${member.revision}`}
                member={member}
                others={others}
                canPublish={can('content.publish')}
                onWrote={(saved) => adopt(page.map((row) => (row.memberId === saved.memberId ? saved : row)))}
                onChanged={() => void reload()}
                onClose={() => setOpen(null)}
              />
            )}
          </div>
        ))}
      </section>

      {archived.length > 0 && (
        <section className="grid gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Off the page ({archived.length})
          </h2>
          <p className="text-xs text-muted-foreground">
            Removed from /team and kept here with their history, so a departure stays auditable.
            Bringing somebody back returns them as a draft at the end of the page — nothing goes
            live again until it has been read.
          </p>
          {archived.map((member) => (
            <ArchivedRow
              key={member.memberId}
              member={member}
              canAuthor={can('content.author')}
              onChanged={() => void reload()}
            />
          ))}
        </section>
      )}
    </ContentPage>
  );
}

/**
 * Somebody off the page, and the one control they have.
 *
 * **Bringing them back makes them a draft, not a live person again.** Nothing here can edit an
 * archived row, so a return that went straight onto the site would mean publishing whatever took
 * them off in order to fix it — a departure is rarely followed by wanting the same words back.
 * They land last among the live, because the page has moved on since they left; the button says
 * both, since a page that silently reinserted somebody into the middle, live, would be a page
 * nobody could explain.
 *
 * It asks for `content.author` rather than `content.publish`: a draft is not public.
 */
function ArchivedRow({
  member,
  canAuthor,
  onChanged
}: {
  member: IContentTeamMember;
  canAuthor: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3 py-2 text-sm">
      <span className="flex-1 truncate">{member.name}</span>
      <StatusChip tone="neutral">
        Removed {formatDateTime(member.updatedAt)} by {personName(member.updatedBy)}
      </StatusChip>
      {canAuthor && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          title="They come back as a draft, at the end of the page, for editing before they go live"
          onClick={async () => {
            setBusy(true);
            try {
              await teamApi.restore(member.memberId, `"${member.revision}"`);
              onChanged();
            } finally {
              setBusy(false);
            }
          }}
        >
          Bring back as a draft
        </Button>
      )}
    </div>
  );
}

/**
 * One person's form, opened under their row.
 *
 * Mounted with a `key` carrying the member's revision, so the form is seeded once per version of
 * them: a save answers the next revision, the key changes, and the form reseeds from what the
 * server actually stored rather than from what was typed.
 *
 * AC2 asks that text and photograph be changeable independently. Only the text half exists — the
 * page says why — and it is independent of everything else about the person, which is the part
 * the criterion is written against: a single "replace this person" flow is what it refuses.
 */
function MemberEditor({
  member,
  others,
  canPublish,
  onWrote,
  onChanged,
  onClose
}: {
  member: IContentTeamMember;
  /** Who else has this person open (WEB-11 AC2), marked by the page. */
  others: IContentPresence[];
  canPublish: boolean;
  /** A write that changed only this person — adopted in place, no re-read. */
  onWrote: (member: IContentTeamMember) => void;
  /** A write that moved the page: an archive closes the gap behind them, a publish may not. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = React.useState<IContentTeamMemberBody>(() => bodyOf(member));
  const [busy, setBusy] = React.useState(false);
  const [conflict, setConflict] = React.useState(false);
  const [refusals, setRefusals] = React.useState<{ target: string; reason: string }[]>([]);
  const [confirming, setConfirming] = React.useState(false);
  const etag = `"${member.revision}"`;
  const dirty = JSON.stringify(form) !== JSON.stringify(bodyOf(member));
  const refusalOf = (target: string) => refusals.find((problem) => problem.target === target);

  const write = async (
    run: () => Promise<{ body: IContentTeamMember }>,
    after: (saved: IContentTeamMember) => void
  ) => {
    setBusy(true);
    setConflict(false);
    setRefusals([]);
    try {
      after((await run()).body);
    } catch (writeError) {
      if (writeError instanceof ApiError && writeError.isProblem(ProblemType.revisionConflict)) {
        setConflict(true);
      } else if (writeError instanceof ApiError && writeError.isProblem(ProblemType.invalidBody)) {
        setRefusals(writeError.problem?.problems ?? []);
      } else {
        setRefusals([{ target: '', reason: 'could not be saved. Try again.' }]);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * The one way out this person has, or none.
   *
   * `publishedAt` decides, not the status: it says whether the page has ever had them, which is
   * what makes a departure worth keeping. A draft nobody ever saw is deleted; anybody else is
   * taken off the page and kept.
   */
  const removal = member.publishedAt === null
    ? {
        label: 'Delete this draft',
        confirm: 'Delete the draft',
        cost:
          'They have never been on the team page, so there is nothing to keep and nothing to ' +
          'undo this with.',
        run: deleteDraft
      }
    : canPublish
      ? {
          label: 'Remove from the page',
          confirm: 'Remove from the page',
          cost:
            member.status === 'published'
              ? 'They come off the live page straight away. Every word and every version is ' +
                'kept, and they can be brought back as a draft afterwards.'
              : 'They go back to "Off the page" with their history. Nothing on the live page ' +
                'changes — they are not on it.',
          run: archive
        }
      : null;

  async function archive() {
    setConfirming(false);
    await write(() => teamApi.archive(member.memberId, etag), () => {
      onClose();
      onChanged();
    });
  }

  async function deleteDraft() {
    setConfirming(false);
    setBusy(true);
    try {
      await teamApi.remove(member.memberId, etag);
      onClose();
      onChanged();
    } catch (deleteError) {
      // A 404 means somebody else already removed them. Saying "try again" would be wrong twice
      // over: there is nothing to try, and the screen is showing a row that no longer exists —
      // which is what the second, third and seventh attempt would each find. Re-read instead.
      if (deleteError instanceof ApiError && deleteError.status === 404) {
        onClose();
        onChanged();
        return;
      }
      setRefusals([
        {
          target: '',
          reason:
            deleteError instanceof ApiError && deleteError.isProblem(ProblemType.revisionConflict)
              ? 'changed while this was open, so nothing was deleted. Reload the page.'
              : 'could not be deleted. Reload the page and try again.'
        }
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4 rounded-md rounded-t-none border border-t-0 border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/20 p-4 shadow-[inset_3px_0_0_var(--color-royal-blue)]">
      <EditPresenceNotice kind="member" others={others} />
      {conflict && (
        <ConflictNotice by={personName(member.updatedBy)} onReload={onChanged} />
      )}
      {refusalOf('') && (
        <p className="text-xs text-[var(--color-deep-magenta)]">
          This person {refusalOf('')!.reason}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`name-${member.memberId}`}>Name</Label>
          <Input
            id={`name-${member.memberId}`}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {refusalOf('name') && (
            <p className="text-xs text-[var(--color-deep-magenta)]">
              This {refusalOf('name')!.reason}
            </p>
          )}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`role-${member.memberId}`}>Role</Label>
          <Input
            id={`role-${member.memberId}`}
            value={form.roleTitle}
            placeholder="Managing Director"
            onChange={(e) => setForm({ ...form, roleTitle: e.target.value })}
          />
          {refusalOf('roleTitle') && (
            <p className="text-xs text-[var(--color-deep-magenta)]">
              This {refusalOf('roleTitle')!.reason}
            </p>
          )}
        </div>
      </div>

      {/* The site's team filters. None is allowed: they are then under "All" only. */}
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-sm font-medium">Groups on the team page</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {TEAM_GROUPS.map(({ code, label }) => (
            <div key={code} className="flex items-center gap-2 text-sm">
              <Checkbox
                id={`group-${code}-${member.memberId}`}
                checked={form.groups.includes(code)}
                onCheckedChange={(next) =>
                  setForm({
                    ...form,
                    // Kept in the list's order, as survey-api stores it, so a ticked-then-cleared
                    // box leaves the form equal to the record and the save button off.
                    groups: TEAM_GROUPS.map((group) => group.code).filter((other) =>
                      other === code ? next === true : form.groups.includes(other)
                    )
                  })
                }
              />
              <label htmlFor={`group-${code}-${member.memberId}`}>{label}</label>
            </div>
          ))}
        </div>
        {refusalOf('groups') && (
          <p className="text-xs text-[var(--color-deep-magenta)]">
            This {refusalOf('groups')!.reason}
          </p>
        )}
      </fieldset>

      {/* WEB-07 AC2's other half, which waited on the media library and no longer does: the
          photograph travels in their content, so it can be changed without touching a word of
          the biography — and a revert brings back the picture that was theirs at the time. */}
      <ImagePicker
        label="Photograph"
        value={form.photoImageId}
        current={member.photo}
        kinds={['headshot', 'photo']}
        onPick={(photoImageId) => setForm({ ...form, photoImageId })}
      />
      {refusalOf('photoImageId') && (
        <p className="text-xs text-[var(--color-deep-magenta)]">
          This {refusalOf('photoImageId')!.reason}
        </p>
      )}

      <BodyEditor
        label="Biography"
        value={form.bio}
        onChange={(value) => setForm({ ...form, bio: value })}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">History</CardTitle>
        </CardHeader>
        <CardContent>
          <HistoryPanel
            history={member.history.map((version) => ({
              versionNo: version.versionNo,
              at: version.at,
              by: personName(version.by),
              action: version.action,
              note: version.note
            }))}
            onRevert={(versionNo) =>
              void write(() => teamApi.revert(member.memberId, etag, versionNo), onWrote)
            }
          />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!dirty || busy}
          onClick={() => void write(() => teamApi.save(member.memberId, etag, form), onWrote)}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {member.status !== 'published' && canPublish && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void write(() => teamApi.publish(member.memberId, etag), () => onChanged())
            }
          >
            Publish to the page
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X className="mr-1 h-4 w-4" aria-hidden />
          Close
        </Button>
        {/* One way out per state, decided by what the page has had of this person rather than by
            what they are right now. Never on it: deleted, because there is no departure to keep.
            Ever on it — live, or a draft somebody brought back — removed from the page, because
            there is. Getting this wrong left a returned draft with no control at all: the archive
            wanted them published and the delete wanted them never published, so neither took
            them. The server refuses the difference; this only draws it. */}
        {removal && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            className="ml-auto text-[var(--color-deep-magenta)]"
            onClick={() => setConfirming(true)}
          >
            <Trash2 className="mr-1 h-4 w-4" aria-hidden />
            {removal.label}
          </Button>
        )}
      </div>

      {/* The house pattern for a destructive write (`pages/lists/save-view.tsx`): a dialog that
          names who goes, says what it costs, and offers a way out. Both removals are asked —
          taking somebody off the live page is seen by readers, and a delete cannot be undone at
          all. */}
      {removal && (
        <AlertDialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
          <AlertDialogContent className="w-[calc(100%-2rem)] max-w-lg">
            <AlertDialogHeader className="gap-1 py-4 pr-12">
              <AlertDialogTitle>
                {removal.label} — {member.name || 'this draft'}?
              </AlertDialogTitle>
              <AlertDialogDescription>{removal.cost}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="px-6 py-3">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => void removal.run()}>
                {removal.confirm}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function bodyOf(member: IContentTeamMember): IContentTeamMemberBody {
  return {
    name: member.name,
    roleTitle: member.roleTitle,
    groups: member.groups,
    bio: member.bio,
    // The form holds the id a write sends; the record holds the image a read answered.
    photoImageId: member.photo?.imageId ?? null
  };
}

function groupLabels(groups: ContentTeamGroup[]): string {
  return TEAM_GROUPS.filter((group) => groups.includes(group.code))
    .map((group) => group.label)
    .join(', ');
}
